import { uuidv4 } from 'lander/cf_utils'
import { CFErrorWithCause, ErrorOptions } from './error_with_cause'
import { pathJoin } from './path'

export const CFFetcherErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  // Every attempt was cut off by our own timeout timer. Distinct from
  // NETWORK_ERROR (the request failed on its own) and from MANUALLY_ABORTED
  // (something cancelled us deliberately).
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
}
globalThis.CFFetcherErrorTypes = CFFetcherErrorTypes
export class CFFetcherError extends CFErrorWithCause {
  public type: string

  constructor(type: string, options?: ErrorOptions) {
    super(type, options)
    this.name = 'CFFetcherError'
    this.type = type
  }
}
globalThis.CFFetcherError = CFFetcherError

type EnhancedFetchOptions = {
  requestId?: string
  retries?: number
  timeoutMS?: number
  timeoutAfterRetrial?: number
  shouldCaptureServerError?: boolean
  convertThrowToErrorResponse?: boolean
  onlyOneInflightRequest?: boolean
  simulateNetworkError?: boolean
  canAbort?: boolean
  // Opt-in, and only safe for idempotent requests. A 5xx normally fails fast so a
  // non-idempotent POST (an order submit) is never replayed into a double charge.
  retryServerErrors?: boolean
  // Opt-in for the same reason. Before per-attempt abort controllers, a timeout poisoned
  // the one shared signal and every later attempt rejected before dispatch, so `retries`
  // silently never applied to timeouts. Fixing that would otherwise start issuing real
  // replay requests on every fetcher in the lander, including order submit.
  retryTimeouts?: boolean
}

interface FetcherOptions extends RequestInit {
  debug?: boolean
  pathPrefix?: string
  requestOptions?: FetcherRequestOptions
}

export type FetcherRequestOptions = {
  callbackData?: any
  customEvent?: string
} & EnhancedFetchOptions

const FetcherRequestDefaultOptions: FetcherRequestOptions = {
  retries: 1,
  timeoutMS: -1,
  timeoutAfterRetrial: 1000,
  shouldCaptureServerError: false,
  convertThrowToErrorResponse: false,
  onlyOneInflightRequest: false,
  simulateNetworkError: false,
  canAbort: true,
  retryServerErrors: false,
  retryTimeouts: false,
}

export enum FetcherResponseErrorType {
  Internal,
  Backend,
}
type FetcherInternalResposeError = {
  fetcherErrorType: FetcherResponseErrorType.Internal
  error: string
}
export type FetcherBackendResposeError = {
  fetcherErrorType: FetcherResponseErrorType.Backend
  error: Record<string, any>
}

export type FetcherResponseError = FetcherInternalResposeError | FetcherBackendResposeError

export type FetcherResponse<T> = T | FetcherResponseError
export function isResponseError<T>(response: FetcherResponse<T>): response is FetcherResponseError {
  return (response as FetcherResponseError).fetcherErrorType != undefined
}
export function isFetcherError(response: FetcherResponseError): response is FetcherInternalResposeError {
  return response.fetcherErrorType == FetcherResponseErrorType.Internal
}
export function isBackendError(response: FetcherResponseError): response is FetcherBackendResposeError {
  return response.fetcherErrorType == FetcherResponseErrorType.Backend
}

export const MANUALLY_ABORTED = 'Manually aborted'
export type InFlightRequest = {
  manuallyAborted: boolean
  // The controller for the attempt currently in flight. `enhancedFetch` swaps this
  // out on every retry, so `abort()` always cancels the attempt that is actually
  // running rather than a spent one.
  abortController: AbortController
}
export default class Fetcher<T> {
  loading: boolean
  controller: AbortController
  signal: AbortSignal
  options: FetcherOptions
  url: string
  inflightRequests: Record<string, InFlightRequest>

  constructor(options?: FetcherOptions) {
    this.options = options || {}
    this.inflightRequests = {}
  }

  /*
   * This fetch call is an extension of original fetch which only fires
   * api with a loading state before and after the call.
   * Can also debounce greater or less than 500ms if required.
   * The fetch request returns the JSON promise and is abortable
   */
  public async fetch(
    url: string,
    init: RequestInit,
    requestOptions: FetcherRequestOptions
  ): Promise<FetcherResponse<Response>> {
    const { callbackData, customEvent, ...enhancedFetchOptions } = {
      ...FetcherRequestDefaultOptions,
      ...(this.options?.requestOptions ?? {}),
      ...(requestOptions ?? {}),
    }

    const { onlyOneInflightRequest, canAbort } = enhancedFetchOptions
    if (this.loading && onlyOneInflightRequest) {
      if (canAbort) {
        this.abort()
      } else {
        return { fetcherErrorType: FetcherResponseErrorType.Internal, error: 'Another request is in progress' }
      }
    }

    const requestId = uuidv4()
    enhancedFetchOptions.requestId = requestId

    this.inflightRequests[requestId] = {
      manuallyAborted: false,
      abortController: new AbortController(),
    }

    let response: Response
    this.url = url
    const { headers, method, credentials } = this.options ?? {}
    // NOTE: no `signal` here on purpose. Each attempt in `enhancedFetch` builds its
    // own controller and passes its own signal, because an AbortSignal stays aborted
    // once it fires: a single shared signal makes every attempt after the first
    // timeout reject instantly, silently defeating `retries`.
    init = {
      ...{ method, credentials },
      ...(init ?? {}),
      headers: {
        ...headers,
        ...init.headers,
      },
    }

    this.setLoading(true, callbackData, customEvent)
    try {
      if (enhancedFetchOptions?.simulateNetworkError) {
        throw new Error('Network Error')
      }
      response = await this.enhancedFetch(url, init, enhancedFetchOptions)
      this.setLoading(false, callbackData, customEvent)
      if (this.options.debug) {
        console.log('[Fetch Request Completed]', response)
      }
      return response
    } catch (error) {
      this.setLoading(false, error, customEvent)
      let err = error
      const inflightRequest = this.inflightRequests[requestId]
      if (inflightRequest.manuallyAborted) {
        err = MANUALLY_ABORTED
      } else if (this.options.debug) {
        // A timeout already arrives here as a CFFetcherError(TIMEOUT_ERROR) built by the
        // attempt that actually timed out. Re-deriving it from shared state would let a
        // spent timer from an earlier attempt relabel a later, unrelated failure.
        console.log('[Error During Fetch]', error)
      }

      if (enhancedFetchOptions.convertThrowToErrorResponse) {
        return { fetcherErrorType: FetcherResponseErrorType.Internal, error: err }
      } else {
        throw err
      }
    } finally {
      delete this.inflightRequests[requestId]
    }
  }

  public async toJSON<T>(response: FetcherResponse<Response>): Promise<FetcherResponse<T>> {
    if (isResponseError(response)) {
      return response
    } else {
      try {
        if (response.ok) {
          return (await response.json()) as FetcherResponse<T>
        } else if ([400, 422].includes(response.status)) {
          return { fetcherErrorType: FetcherResponseErrorType.Backend, error: await response.json() }
        } else {
          throw new CFFetcherError(CFFetcherErrorTypes.SERVER_ERROR, {
            cause: new Error(
              `Invalid response from server\n  Status: ${response.status}\n  Body: ${await response.text()}`
            ),
          })
        }
      } catch (error) {
        if (this.options.requestOptions.convertThrowToErrorResponse) {
          return { fetcherErrorType: FetcherResponseErrorType.Internal, error }
        } else {
          throw error
        }
      }
    }
  }

  public async post(
    url: string,
    data?: Record<string, unknown>,
    requestOptions?: FetcherRequestOptions,
    init?: RequestInit
  ): Promise<FetcherResponse<T>> {
    const response = await this.fetch(
      url,
      {
        method: 'POST',
        body: JSON.stringify(data),
        ...(init ?? {}),
      },
      requestOptions
    )
    return await this.toJSON<T>(response)
  }

  public async get(
    url: string,
    data?: Record<string, any>,
    requestOptions?: FetcherRequestOptions,
    init?: RequestInit
  ): Promise<FetcherResponse<T>> {
    let fullUrl = url
    const queryParams = Object.entries(data ?? {})
      .filter(([, v]) => !!v)
      .map(([k, v]) => `${k}=${v}`)
      .join('&')

    if (queryParams) {
      fullUrl = `${fullUrl}?${queryParams}`
    }

    const response = await this.fetch(
      fullUrl,
      {
        method: 'GET',
        ...(init ?? {}),
      },
      requestOptions
    )

    return this.toJSON(response)
  }

  async enhancedFetch(url: string, init: RequestInit, opts: EnhancedFetchOptions): Promise<Response> {
    const {
      retries,
      timeoutMS,
      timeoutAfterRetrial,
      shouldCaptureServerError,
      retryServerErrors,
      retryTimeouts,
      requestId,
    } = opts
    let lastErr
    for (let i = 0; i < retries; i++) {
      try {
        if (i > 0) {
          await new Promise((resolve) => setTimeout(resolve, timeoutAfterRetrial))
        }
        if (shouldCaptureServerError) {
          return await this.fetchWithTimeout(url, init, timeoutMS, requestId).then((response: Response) => {
            if (response.status >= 500) {
              throw new CFFetcherError(CFFetcherErrorTypes.SERVER_ERROR)
            }
            return response
          })
        } else {
          return await this.fetchWithTimeout(url, init, timeoutMS, requestId)
        }
      } catch (err) {
        if (err instanceof CFFetcherError && err.type == CFFetcherErrorTypes.SERVER_ERROR && !retryServerErrors) {
          throw err
        }
        // Same gate for timeouts. A timed-out POST may well have reached the server and be
        // running there, so replaying it is only safe where the caller says so.
        if (err instanceof CFFetcherError && err.type == CFFetcherErrorTypes.TIMEOUT_ERROR && !retryTimeouts) {
          throw err
        }
        // An abort we did not schedule ourselves — `abort()` from the caller, or a
        // superseding request under `onlyOneInflightRequest` — means nobody is
        // waiting for this result any more. Retrying it would race the request that
        // replaced us, so give up and let `fetch` report it as MANUALLY_ABORTED.
        if (this.inflightRequests[requestId]?.manuallyAborted) {
          throw err
        }
        lastErr = err
      }
    }

    // Keep an already-classified failure (e.g. a retried SERVER_ERROR) as-is so
    // exhausting the retries does not relabel it as a network failure.
    if (lastErr instanceof CFFetcherError) throw lastErr
    throw new CFFetcherError(CFFetcherErrorTypes.NETWORK_ERROR, { cause: lastErr })
  }

  fetchWithTimeout(url: string, init: RequestInit, timeoutDuration = 1000, requestId: string): Promise<Response> {
    const inflightRequest = this.inflightRequests[requestId]
    // A fresh controller per attempt. The previous attempt's controller is already
    // aborted (that is why we are retrying), and an aborted signal can never be
    // reused.
    const abortController = new AbortController()
    if (inflightRequest) {
      if (inflightRequest.manuallyAborted) abortController.abort()
      inflightRequest.abortController = abortController
    }

    // Everything about the timeout stays local to this attempt: the flag, the timer, and
    // the clearing of it. Nothing is written to the shared in-flight record, so a timer
    // belonging to a spent attempt cannot latch a timeout onto the one now running and
    // mislabel it.
    let timedOut = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    if (timeoutDuration > 0) {
      timeoutId = setTimeout(() => {
        timedOut = true
        abortController.abort()
      }, timeoutDuration)
    }

    const finalUrl = url.startsWith('http') || url.startsWith('https') ? url : pathJoin(this.options.pathPrefix, url)
    return fetch(finalUrl, { ...init, signal: abortController.signal })
      .catch((error) => {
        // Classified at the point of failure, so callers get the label by construction
        // rather than by inspecting state that a later attempt may have overwritten.
        if (timedOut) throw new CFFetcherError(CFFetcherErrorTypes.TIMEOUT_ERROR, { cause: error })
        throw error
      })
      .finally(() => clearTimeout(timeoutId))
  }

  public abort(requestId?: string): void {
    const requestsToAborts = requestId ? [requestId] : Object.keys(this.inflightRequests) ?? []
    requestsToAborts.forEach((key) => {
      const inflightRequest = this.inflightRequests[key]
      if (!inflightRequest) return
      if (!inflightRequest?.manuallyAborted) {
        if (this.options.debug) {
          console.log(`[Aborting Request][RequestID=${key}]`, this.url)
        }
        inflightRequest.abortController.abort()
        inflightRequest.manuallyAborted = true
      }
    })
  }

  public setLoading(isLoading: boolean, details: Record<string, unknown>, customName: string): void {
    let loadingEvent: CustomEvent
    const startEvent = (customName && customName + 'Started') || 'CFFetchStarted'
    const endEvent = (customName && customName + 'Finished') || 'CFFetchFinished'

    if (isLoading && !this.loading) {
      if (this.options.debug) {
        console.log('[Loading Started]', startEvent)
      }
      this.loading = true
      loadingEvent = new CustomEvent(startEvent, {
        detail: details,
      })
    } else if (!isLoading && this.loading) {
      if (this.options.debug) {
        console.log('[Loading Finished/Aborted]', endEvent)
      }

      this.loading = false
      loadingEvent = new CustomEvent(endEvent, {
        detail: details,
      })
    }

    if (loadingEvent) {
      document.dispatchEvent(loadingEvent)
    }
  }
}

globalThis.CFFetcher = globalThis.CFFetcher || Fetcher
export const CFFetch = async (
  url: string,
  data: RequestInit,
  requestOptions: FetcherRequestOptions
): Promise<Response> => {
  const fetcher = new Fetcher()
  return (await fetcher.fetch(url, data, requestOptions)) as Response
}
globalThis.CFFetch = CFFetch
